import { Component, ElementRef, EventEmitter, inject, Input, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IonicModule, ModalController } from '@ionic/angular';
import { Capacitor } from '@capacitor/core';

import { ImageCropperModal } from './image-cropper.modal';
import { croppedBlobOf } from './cropper-result';

/**
 * Shared component for capturing / selecting a user photo and cropping it.
 *
 * ## Usage
 * ```html
 * <abs-photo-capture
 *     [currentImageUrl]="imageService.url(unit.userImageId)"
 *     [disabled]="false"
 *     (photoSelected)="onPhoto($event)"
 *     (photoRemoved)="onRemove()"
 * ></abs-photo-capture>
 * ```
 *
 * On native (iOS/Android) uses `@capacitor/camera` to prompt camera/gallery.
 * On web uses a file input with `accept="image/*"`.
 * After selection, opens `ImageCropperModal` for square crop.
 * Emits the cropped Blob via `photoSelected`.
 */
@Component({
    standalone: true,
    imports: [CommonModule, IonicModule],
    selector: 'abs-photo-capture',
    template: `
        <div class="photoCaptureWrap">
            @if (currentImageUrl) {
                <div class="previewWrap">
                    @if (!hidePreview) {
                        <img [src]="currentImageUrl" alt="Unit photo" class="preview" (error)="onImageError($event)" />
                    }
                    <div class="previewActions">
                        <button class="captureBtn" (click)="selectImage()" [disabled]="disabled || uploading">
                            <ion-icon name="camera-outline"></ion-icon>
                            Change
                        </button>
                        <button class="captureBtn removeBtn" (click)="remove()" [disabled]="disabled || uploading">
                            <ion-icon name="trash-outline"></ion-icon>
                            Remove
                        </button>
                    </div>
                </div>
            } @else {
                <button class="captureBtn addBtn" (click)="selectImage()" [disabled]="disabled || uploading">
                    @if (uploading) {
                        <ion-spinner name="crescent" class="uploadSpinner"></ion-spinner>
                        Uploading…
                    } @else {
                        <ion-icon name="camera-outline"></ion-icon>
                        Add Photo
                    }
                </button>
            }
        </div>

        <!-- Hidden file input for web / mobile-browser fallback.
             accept="image/*" ensures mobile browsers offer the camera option. -->
        <input #fileInput type="file" accept="image/*" style="display: none" (change)="onFileSelected($event)" />
    `,
    styles: [
        `
            .photoCaptureWrap {
                display: flex;
                align-items: center;
                gap: 12px;
            }

            .previewWrap {
                display: flex;
                align-items: center;
                gap: 12px;
            }

            .preview {
                width: 64px;
                height: 64px;
                object-fit: cover;
                border-radius: 6px;
                border: 1px solid rgba(0, 0, 0, 0.12);
            }

            .previewActions {
                display: flex;
                gap: 8px;
            }

            .captureBtn {
                display: inline-flex;
                align-items: center;
                gap: 5px;
                padding: 6px 12px;
                border-radius: 6px;
                border: 1px solid var(--btn-color, #00a03e);
                background: var(--btn-color, #00a03e);
                color: #fff;
                font-size: 0.82em;
                font-weight: 600;
                cursor: pointer;
                white-space: nowrap;
                transition:
                    background 0.15s,
                    border-color 0.15s;
            }

            .captureBtn:hover:not(:disabled) {
                background: var(--btn-color-hover, #008533);
                border-color: var(--btn-color-hover, #008533);
            }

            .captureBtn:disabled {
                opacity: 0.45;
                cursor: default;
            }

            .captureBtn ion-icon {
                font-size: 1.15em;
            }

            .removeBtn {
                background: transparent;
                border-color: rgba(0, 0, 0, 0.2);
                color: #666;
            }

            .removeBtn:hover:not(:disabled) {
                background: rgba(200, 0, 0, 0.08);
                border-color: rgba(200, 0, 0, 0.3);
                color: #a00;
            }

            .addBtn {
                padding: 8px 16px;
            }

            .uploadSpinner {
                width: 16px;
                height: 16px;
                --color: #fff;
            }
        `
    ]
})
export class PhotoCaptureComponent {
    private modalController = inject(ModalController);

    @ViewChild('fileInput') private fileInputRef!: ElementRef<HTMLInputElement>;

    @Input() currentImageUrl: string | null = null;
    @Input() disabled = false;
    @Input() uploading = false;
    /** When true the internal thumbnail is hidden — useful when the host component
     *  already renders the image itself and only needs the capture/remove buttons. */
    @Input() hidePreview = false;

    /**
     * Output crop size. Left unset the cropper uses its own square default, which is
     * what a unit thumbnail wants; a wide image such as an event banner passes its own
     * dimensions so the frame matches where the image will actually be shown.
     */
    @Input() cropWidth?: number;
    @Input() cropHeight?: number;
    /** Below 1 lets the user zoom out — useful when the source is smaller than the frame. */
    @Input() minZoom?: number;
    /** Output format for the cropped blob. Left unset the modal keeps its own PNG default —
     * this component has no opinion on format; the caller decides based on its content. */
    @Input() exportFormat?: 'png' | 'jpeg' | 'webp';

    @Output() photoSelected = new EventEmitter<Blob>();
    @Output() photoRemoved = new EventEmitter<void>();

    async selectImage() {
        if (this.disabled || this.uploading) return;

        if (Capacitor.isNativePlatform()) {
            const base64 = await this.captureNative();
            if (base64) {
                await this.openCropper(base64);
            }
        } else {
            this.fileInputRef?.nativeElement.click();
            // Flow continues in onFileSelected
        }
    }

    async onFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0];
        if (!file) return;

        // Reset input so the same file can be re-selected
        input.value = '';

        const base64 = await this.fileToBase64(file);
        await this.openCropper(base64);
    }

    remove() {
        this.photoRemoved.emit();
    }

    onImageError(event: Event) {
        const img = event.target as HTMLImageElement;
        if (img) img.style.display = 'none';
    }

    private async captureNative(): Promise<string | null> {
        try {
            // Dynamic import — only loaded on native platforms
            const { Camera, CameraResultType, CameraSource } = await import('@capacitor/camera');
            const photo = await Camera.getPhoto({
                resultType: CameraResultType.Base64,
                source: CameraSource.Prompt,
                quality: 90,
                allowEditing: false
            });
            if (photo.base64String) {
                const mimeType = photo.format === 'png' ? 'image/png' : 'image/jpeg';
                return `data:${mimeType};base64,${photo.base64String}`;
            }
            return null;
        } catch (err: any) {
            // User cancelled — silent. Anything else (plugin missing, permission denied) — log it.
            const isCancelled = err?.message === 'User cancelled photos app' || err?.errorMessage === 'No image picked';
            if (!isCancelled) {
                console.warn('PhotoCapture: native camera unavailable, falling back to file input.', err);
                // Fall back to file input (works on Simulator and when Camera plugin is missing)
                this.fileInputRef?.nativeElement.click();
            }
            return null;
        }
    }

    private async openCropper(imageBase64: string) {
        const modal = await this.modalController.create({
            component: ImageCropperModal,
            // Only pass dimensions that were actually supplied — the modal's own
            // defaults (512 square, minZoom 1) must stand for every existing caller.
            componentProps: {
                imageBase64,
                ...(this.cropWidth ? { cropWidth: this.cropWidth, fixedOutputSize: true } : {}),
                ...(this.cropHeight ? { cropHeight: this.cropHeight } : {}),
                ...(this.minZoom ? { minZoom: this.minZoom } : {}),
                ...(this.exportFormat ? { exportFormat: this.exportFormat } : {})
            },
            backdropDismiss: false
        });

        await modal.present();
        // The modal dismisses { blob, zoom, transform, ... } so a caller can re-crop
        // later. This component's contract is a Blob, so unwrap here rather than
        // making every consumer know the modal's shape — they were passing the whole
        // object to FormData.append() and throwing "parameter 2 is not of type 'Blob'".
        const { data } = await modal.onDidDismiss();
        const blob = croppedBlobOf(data);

        if (blob) {
            this.photoSelected.emit(blob);
        }
    }

    private fileToBase64(file: File): Promise<string> {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result as string);
            reader.onerror = reject;
            reader.readAsDataURL(file);
        });
    }
}
